import React, { useState, useEffect } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { MESSAGES } from '~/utils/message';

// Actions
import { doublicateTemplate, getAllTemplates } from '~/redux/actions/template-builder';
import { failure, success } from '~/redux/actions/snackbar-actions';

// MUI Components
import IconButton from '@mui/material/IconButton';
import { CircularProgress, InputAdornment, InputLabel, TextField } from '@mui/material';

// Icons
import { DetailsCrossCIcon } from '~/assets/images';

// Styles
import './styles.scss';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

/**
 * DuplicateTemplateModal Component
 * A modal for duplicating a template.
 *
 * @param {boolean} open - Boolean indicating whether the modal is open.
 * @param {Function} handleClose - Function to handle the closing of the modal.
 * @param {Object} selectedRecord - The selected template record to duplicate.
 * @returns {JSX.Element} The duplicate template modal component.
 *
 */
const DuplicateTemplateModal = ({ open = false, handleClose, selectedRecord }) => {
  const formId = 'duplicate-template-form';
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const templates = useSelector((state) => state.templateReducer.templates);

  // Handle the duplication of the template
  const handleDuplicate = async () => {
    const trimedTitle = title.trim();
    const errorText = !trimedTitle
      ? MESSAGES.TEMPLATE.NAME_REQUIRED
      : trimedTitle.length > 50
        ? MESSAGES.TEMPLATE.NAME_LESS_50
        : '';
    if (errorText) {
      dispatch(failure(errorText));
      return;
    }
    setLoading(true);
    const response = await doublicateTemplate(selectedRecord.id, {
      title: trimedTitle,
    });
    setLoading(false);

    if (response.status === 200) {
      dispatch(success(response.data.message));
      dispatch(getAllTemplates(templates.page, templates.perPage));
      handleCloseDialog();
    } else {
      dispatch(failure(response.data.message));
    }
  };

  useEffect(() => {
    setTitle(`${selectedRecord?.title} (Copy)`);
  }, [selectedRecord?.title]);

  // Close the modal
  const handleCloseDialog = () => handleClose(false);

  return (
    <OlcFormModal
      open={open}
      onClose={handleCloseDialog}
      title="Duplicate Template"
      className="duplicateTemplateModal"
      footer={
        <>
          <OlcButton
            variant="secondary"
            type="button"
            disabled={loading}
            onClick={handleCloseDialog}
          >
            Cancel
          </OlcButton>
          <OlcButton variant="primary" type="submit" form={formId} disabled={loading}>
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              'Save'
            )}
          </OlcButton>
        </>
      }
    >
      <form
        id={formId}
        className="duplicateTemplateForm"
        onSubmit={(event) => {
          event.preventDefault();
          handleDuplicate();
        }}
        noValidate
      >
        <InputLabel htmlFor="duplicate-template-name" className="duplicateTemplateLabel">
          Template Name
        </InputLabel>
        <TextField
          id="duplicate-template-name"
          type="text"
          fullWidth
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={selectedRecord?.title}
          InputProps={{
            endAdornment: title ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear template name"
                  onClick={() => setTitle('')}
                  disabled={loading}
                  disableRipple
                  className="duplicateTemplateClear"
                >
                  <img src={DetailsCrossCIcon} alt="" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </form>
    </OlcFormModal>
  );
};

export default DuplicateTemplateModal;
